import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * The store itself: what it is called, where it is, and how it hands orders over.
 *
 * A *business* holds one or more *locations*, and nearly everything on this
 * page belongs to the first location rather than to the business — the
 * address, the phone, the pickup rules. The two are edited together because
 * nobody thinks of them as two things.
 *
 * **The update is not the DTO.** `UpdateBusinessBindingModel` is a different
 * shape from `BusinessDto`, and its nested `UpdateLocationDto` has
 * non-nullable booleans and integers: anything left out is written as `false`
 * or `0`. So the whole location is rebuilt from what was read, and only then
 * edited. The original posted the DTO straight back, which sent fields the
 * model does not have and omitted several it does.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `FeeType`. */
export const FEE_TYPES = { none: 0, fixed: 1, percentage: 2 } as const;

/** `BusinessLocationDto`, as far as this page reads it. */
export interface BusinessLocation {
  id: string;
  name: string | null;
  address1: string | null;
  address2: string | null;
  directionsText: string | null;
  city: string | null;
  orderEmail: string | null;
  orderPhone: string | null;
  phone: string | null;
  smsAudienceId: string | null;
  geo: { lat: number; lon: number } | null;
  isActive: boolean;
  isDeliveryAvailable: boolean;
  isTakeOutAvailable: boolean;
  isPickUpAvailable: boolean;
  preventTodayPickupSlots: boolean;
  hasSeatedDinning: boolean;
  receiveEmail: boolean;
  receiveSms: boolean;
  sendEmailNotifications: boolean;
  /** The DTO capitalises the I; the update model does not. See `toLocationInput`. */
  posIntegrationActive: boolean;
  slotRange: number;
  pickupStartHour: number;
  pickupEndHour: number;
  pickupLimit: number;
  pickupBuffer: number;
  pickupFee: number;
  pickupFeeType: number;
  futureDaysForPickup: number;
  additionalOrderItemsLimit: number;
  operatingHours: string | null;
  showHealthyDepartment: boolean;
  showRecentlyAddedDepartment: boolean;
  showOffersDepartment: boolean;
  showSponsoredDepartment: boolean;
  showMadeLocallyDepartment: boolean;
  showCustomDepartment01: boolean;
  showCustomDepartment02: boolean;
  showCustomDepartment03: boolean;
  showCustomDepartment04: boolean;
  showCustomDepartment05: boolean;
  showCustomDepartment06: boolean;
  customDepartment01Name: string | null;
  customDepartment02Name: string | null;
  customDepartment03Name: string | null;
  customDepartment04Name: string | null;
  customDepartment05Name: string | null;
  customDepartment06Name: string | null;
  paypalClientId: string | null;
  paypalClientSecret: string | null;
}

/** `BusinessDto`, as far as this page reads it. */
export interface BusinessInfo {
  id: string;
  name: string | null;
  shortName: string | null;
  description: string | null;
  backgroundImage: string | null;
  logoImage: string | null;
  menuHeaderImage: string | null;
  webHeaderImage: string | null;
  isActive: boolean;
  isAlpha: boolean;
  isBeta: boolean;
  visibleInBeta: boolean;
  allowDelivery: boolean;
  allowTakeOut: boolean;
  hasSelfDelivery: boolean;
  pricesIncludeTax: boolean;
  sendDailyReport: boolean;
  reportEmails: string | null;
  allowPromoDateUpdates: boolean;
  requiresFullCatalogUpdate: boolean;
  weMissYouActivated: boolean;
  allowPaypalTransfer: boolean;
  stripeDailyTransfers: boolean;
  payPalDailyTransfers: boolean;
  allowStripePayouts: boolean;
  minimumDeliveryOrderAmount: number;
  minimumPickupOrderAmount: number;
  hasLayaway: boolean;
  layawayServiceFee: number;
  layawayServiceFeeType: number;
  layawayInitialPayment: number;
  layawayInitialPaymentType: number;
  layawayPaymentMinimum: number;
  layawayPaymentMinimumType: number;
  layawayDefaultPenalty: number;
  layawayDefaultPenaltyType: number;
  layawayExpirationDays: number;
  maxLayawaysPerCustomer: number;
  locations: BusinessLocation[] | null;
}

/** `UpdateLocationDto` — every field, because most of them are not nullable. */
export interface LocationInput {
  id: string;
  name: string | null;
  address1: string | null;
  address2: string | null;
  directionsText: string | null;
  orderEmail: string | null;
  orderPhone: string | null;
  phone: string | null;
  smsAudienceId: string | null;
  lat: string | null;
  lon: string | null;
  isActive: boolean;
  isDeliveryAvailable: boolean;
  isTakeOutAvailable: boolean;
  isPickUpAvailable: boolean;
  preventTodayPickupSlots: boolean;
  hasSeatedDinning: boolean;
  receiveEmail: boolean;
  receiveSms: boolean;
  sendEmailNotifications: boolean;
  posintegrationActive: boolean;
  slotRange: number;
  pickupStartHour: number;
  pickupEndHour: number;
  pickupLimit: number;
  pickupBuffer: number;
  pickupFee: number;
  pickupFeeType: number;
  futureDaysForPickup: number;
  additionalOrderItemsLimit: number;
  operatingHours: string | null;
  showHealthyDepartment: boolean;
  showRecentlyAddedDepartment: boolean;
  showOffersDepartment: boolean;
  showSponsoredDepartment: boolean;
  showMadeLocallyDepartment: boolean;
  showCustomDepartment01: boolean;
  showCustomDepartment02: boolean;
  showCustomDepartment03: boolean;
  showCustomDepartment04: boolean;
  showCustomDepartment05: boolean;
  showCustomDepartment06: boolean;
  customDepartment01Name: string | null;
  customDepartment02Name: string | null;
  customDepartment03Name: string | null;
  customDepartment04Name: string | null;
  customDepartment05Name: string | null;
  customDepartment06Name: string | null;
  paypalClientId: string | null;
  paypalClientSecret: string | null;
}

/** `UpdateBusinessBindingModel`. */
export interface BusinessInput {
  id: string;
  name: string | null;
  description: string | null;
  backgroundImage: string | null;
  logoImage: string | null;
  menuHeaderImage: string | null;
  webHeaderImage: string | null;
  isActive: boolean;
  isAlpha: boolean;
  isBeta: boolean;
  visibleInBeta: boolean;
  allowDelivery: boolean;
  allowTakeOut: boolean;
  hasSelfDelivery: boolean;
  pricesIncludeTax: boolean;
  sendDailyReport: boolean;
  reportEmails: string | null;
  allowPromoDateUpdates: boolean;
  requiresFullCatalogUpdate: boolean;
  weMissYouActivated: boolean;
  allowPaypalTransfer: boolean;
  stripeDailyTransfers: boolean;
  payPalDailyTransfers: boolean;
  allowStripePayouts: boolean;
  minimumDeliveryOrderAmount: number;
  minimumPickupOrderAmount: number;
  hasLayaway: boolean;
  layawayServiceFee: number;
  layawayServiceFeeType: number;
  layawayInitialPayment: number;
  layawayInitialPaymentType: number;
  layawayPaymentMinimum: number;
  layawayPaymentMinimumType: number;
  layawayDefaultPenalty: number;
  layawayDefaultPenaltyType: number;
  layawayExpirationDays: number;
  maxLayawaysPerCustomer: number;
  locations: LocationInput[];
}

/**
 * `BusinessLocationDto` → `UpdateLocationDto`.
 *
 * Two things are not a straight copy: `geo.lat`/`geo.lon` are numbers on the
 * way in and strings on the way out, and `posIntegrationActive` loses its
 * capital I. Both are the server's spelling, not a slip here.
 */
export function toLocationInput(location: BusinessLocation): LocationInput {
  return {
    id: location.id,
    name: location.name,
    address1: location.address1,
    address2: location.address2,
    directionsText: location.directionsText,
    orderEmail: location.orderEmail,
    orderPhone: location.orderPhone,
    phone: location.phone,
    smsAudienceId: location.smsAudienceId,
    lat: location.geo ? String(location.geo.lat) : null,
    lon: location.geo ? String(location.geo.lon) : null,
    isActive: location.isActive,
    isDeliveryAvailable: location.isDeliveryAvailable,
    isTakeOutAvailable: location.isTakeOutAvailable,
    isPickUpAvailable: location.isPickUpAvailable,
    preventTodayPickupSlots: location.preventTodayPickupSlots,
    hasSeatedDinning: location.hasSeatedDinning,
    receiveEmail: location.receiveEmail,
    receiveSms: location.receiveSms,
    sendEmailNotifications: location.sendEmailNotifications,
    posintegrationActive: location.posIntegrationActive,
    slotRange: location.slotRange,
    pickupStartHour: location.pickupStartHour,
    pickupEndHour: location.pickupEndHour,
    pickupLimit: location.pickupLimit,
    pickupBuffer: location.pickupBuffer,
    pickupFee: location.pickupFee,
    pickupFeeType: location.pickupFeeType,
    futureDaysForPickup: location.futureDaysForPickup,
    additionalOrderItemsLimit: location.additionalOrderItemsLimit,
    operatingHours: location.operatingHours,
    showHealthyDepartment: location.showHealthyDepartment,
    showRecentlyAddedDepartment: location.showRecentlyAddedDepartment,
    showOffersDepartment: location.showOffersDepartment,
    showSponsoredDepartment: location.showSponsoredDepartment,
    showMadeLocallyDepartment: location.showMadeLocallyDepartment,
    showCustomDepartment01: location.showCustomDepartment01,
    showCustomDepartment02: location.showCustomDepartment02,
    showCustomDepartment03: location.showCustomDepartment03,
    showCustomDepartment04: location.showCustomDepartment04,
    showCustomDepartment05: location.showCustomDepartment05,
    showCustomDepartment06: location.showCustomDepartment06,
    customDepartment01Name: location.customDepartment01Name,
    customDepartment02Name: location.customDepartment02Name,
    customDepartment03Name: location.customDepartment03Name,
    customDepartment04Name: location.customDepartment04Name,
    customDepartment05Name: location.customDepartment05Name,
    customDepartment06Name: location.customDepartment06Name,
    paypalClientId: location.paypalClientId,
    paypalClientSecret: location.paypalClientSecret,
  };
}

/** Every location travels, not only the edited one, or the rest are dropped. */
export function toBusinessInput(business: BusinessInfo): BusinessInput {
  return {
    id: business.id,
    name: business.name,
    description: business.description,
    backgroundImage: business.backgroundImage,
    logoImage: business.logoImage,
    menuHeaderImage: business.menuHeaderImage,
    webHeaderImage: business.webHeaderImage,
    isActive: business.isActive,
    isAlpha: business.isAlpha,
    isBeta: business.isBeta,
    visibleInBeta: business.visibleInBeta,
    allowDelivery: business.allowDelivery,
    allowTakeOut: business.allowTakeOut,
    hasSelfDelivery: business.hasSelfDelivery,
    pricesIncludeTax: business.pricesIncludeTax,
    sendDailyReport: business.sendDailyReport,
    reportEmails: business.reportEmails,
    allowPromoDateUpdates: business.allowPromoDateUpdates,
    requiresFullCatalogUpdate: business.requiresFullCatalogUpdate,
    weMissYouActivated: business.weMissYouActivated,
    allowPaypalTransfer: business.allowPaypalTransfer,
    stripeDailyTransfers: business.stripeDailyTransfers,
    payPalDailyTransfers: business.payPalDailyTransfers,
    allowStripePayouts: business.allowStripePayouts,
    minimumDeliveryOrderAmount: business.minimumDeliveryOrderAmount,
    minimumPickupOrderAmount: business.minimumPickupOrderAmount,
    hasLayaway: business.hasLayaway,
    layawayServiceFee: business.layawayServiceFee,
    layawayServiceFeeType: business.layawayServiceFeeType,
    layawayInitialPayment: business.layawayInitialPayment,
    layawayInitialPaymentType: business.layawayInitialPaymentType,
    layawayPaymentMinimum: business.layawayPaymentMinimum,
    layawayPaymentMinimumType: business.layawayPaymentMinimumType,
    layawayDefaultPenalty: business.layawayDefaultPenalty,
    layawayDefaultPenaltyType: business.layawayDefaultPenaltyType,
    layawayExpirationDays: business.layawayExpirationDays,
    maxLayawaysPerCustomer: business.maxLayawaysPerCustomer,
    locations: (business.locations ?? []).map(toLocationInput),
  };
}

export function useBusinessInfo(businessId: string | null) {
  return useQuery({
    queryKey: ['business-info', businessId],
    queryFn: () => get<BusinessInfo>(`businesses/v2/${businessId}`),
    enabled: !!businessId,
  });
}

export function useUpdateBusinessInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BusinessInput) =>
      unwrap<BusinessInfo>(api.put(`businesses/${input.id}`, input)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-info'] });
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}

/**
 * Upload an image to a named blob container.
 *
 * `utility/{containerName}/uploadfile` answers the public URL as `data`; the
 * background image goes to `backgroundimages`, which is the container the
 * original used.
 */
export function useUploadImage(containerName: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post(`utility/${containerName}/uploadfile`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
